import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCart, Heart, Bell, ImageOff, Share2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'sonner';

export default function ProductCard({ product, price, onAddToCart }) {
  const { user } = useAuth();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isNotifyRequested, setIsNotifyRequested] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Determine availability status (same logic as ProductDetailPage)
  const getAvailabilityStatus = () => {
    if (product.is_sold_out) return { available: false, isSoldOut: true };
    if (product.is_by_order) return { available: true, isSoldOut: false, isByOrder: true };
    const stock = product.stock_quantity ?? 0;
    if (stock > 0) return { available: true, isSoldOut: false };
    return { available: false, isSoldOut: true };
  };

  const availability = getAvailabilityStatus();
  const isUnavailable = !availability.available;

  const checkWishlistStatus = async () => {
    try {
      const res = await api.get(`/wishlist/check/${product.id}`);
      setIsWishlisted(res.data.is_wishlisted);
    } catch (error) {
      // Ignore errors for non-logged in users
    }
  };

  useEffect(() => {
    if (user) {
      checkWishlistStatus();
    }
  }, [user, product.id]);

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to add to wishlist');
      return;
    }

    try {
      if (isWishlisted) {
        await api.delete(`/wishlist/${product.id}`);
        setIsWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await api.post(`/wishlist/${product.id}`);
        setIsWishlisted(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      toast.error('Failed to update wishlist');
    }
  };

  const handleShare = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const shareUrl = `${window.location.origin}/product/${product.id}`;
    const shareData = { title: product.name, text: `Check out ${product.name} on Marissa Gold!`, url: shareUrl };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied to clipboard!');
      }
    }
  };

  const handleNotifyMe = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast.error('Please login to get notified');
      return;
    }

    try {
      await api.post(`/products/${product.id}/notify-me`);
      setIsNotifyRequested(true);
      toast.success('You will be notified when this product is back in stock');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to subscribe for notifications');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`group bg-white rounded-xl sm:rounded-2xl overflow-hidden product-card-hover border shadow-sm hover:shadow-xl transition-all duration-300 h-full flex flex-col ${
        isUnavailable ? 'border-gray-300' : 'border-gray-100 hover:border-[#C9A967]/30'
      }`}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`}>
        <div className="aspect-square overflow-hidden bg-gray-100 relative">
          {!imageLoaded && !imageError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <div className="animate-pulse w-8 h-8 bg-gray-200 rounded-full"></div>
            </div>
          )}
          {imageError ? (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              <div className="text-center text-gray-400">
                <ImageOff className="h-12 w-12 mx-auto mb-2" />
                <p className="text-xs">Image unavailable</p>
              </div>
            </div>
          ) : (
            <img
              src={product.images[0]}
              alt={product.name}
              className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${
                isUnavailable ? 'opacity-50 grayscale' : ''
              } ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
              style={product.image_focal_points?.[0] ? { objectPosition: `${product.image_focal_points[0].x}% ${product.image_focal_points[0].y}%` } : undefined}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              loading="lazy"
            />
          )}
          {isUnavailable && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="bg-red-600 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-full font-semibold text-xs sm:text-sm">
                {product.is_sold_out ? 'SOLD OUT' : 'OUT OF STOCK'}
              </span>
            </div>
          )}
          <div className="absolute top-2 right-2 sm:top-3 sm:right-3 flex flex-col gap-1.5 sm:gap-2">
            <button
              onClick={toggleWishlist}
              className={`p-1.5 sm:p-2 rounded-full transition-all ${
                isWishlisted 
                  ? 'bg-red-500 text-white' 
                  : 'bg-white/80 text-gray-600 hover:bg-white hover:text-red-500'
              }`}
              data-testid={`wishlist-btn-${product.id}`}
            >
              <Heart className={`h-4 w-4 sm:h-5 sm:w-5 ${isWishlisted ? 'fill-current' : ''}`} />
            </button>
            <button
              onClick={handleShare}
              className="p-1.5 sm:p-2 rounded-full bg-white/80 text-gray-600 hover:bg-white hover:text-[#1a3a5c] transition-all"
              data-testid={`share-btn-${product.id}`}
            >
              <Share2 className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </Link>

      <div className="p-3 sm:p-5 flex flex-col flex-grow">
        <Link to={`/product/${product.id}`}>
          <h3 className="font-serif text-sm sm:text-lg font-semibold text-[#1a3a5c] mb-0.5 sm:mb-1 tracking-tight group-hover:text-[#C9A967] transition-colors line-clamp-1 h-5 sm:h-7" data-testid={`product-name-${product.id}`}>
            {product.name}
          </h3>
        </Link>
        <p className="text-xs sm:text-sm text-gray-500 mb-0.5 sm:mb-1 truncate">
          {product.category} • {product.metal_type}{product.metal_carat ? ` ${product.metal_carat}` : ''}
        </p>
        <p className="text-xs text-gray-400 mb-2 sm:mb-3 truncate">
          {product.model_category && `${product.model_category} • `}{price ? `${price.grossWeight}g` : `${product.weight}g`}
        </p>

        <div className="flex-grow"></div>

        {price && (
          <div className="mb-2 sm:mb-4">
            <p className={`text-lg sm:text-2xl font-bold ${isUnavailable ? 'text-gray-400' : 'text-[#1a3a5c]'}`} data-testid={`product-price-${product.id}`}>
              ₹{price.totalPrice}
            </p>
            <p className="text-xs text-gray-500 hidden sm:block">Inc. all taxes</p>
          </div>
        )}

        {isUnavailable ? (
          <button
            onClick={handleNotifyMe}
            disabled={isNotifyRequested}
            className={`w-full py-2 sm:py-3 rounded-full font-medium transition-all duration-300 flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-base mt-auto ${
              isNotifyRequested 
                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                : 'bg-[#C9A967] text-white hover:bg-[#B8985A]'
            }`}
            data-testid={`notify-btn-${product.id}`}
          >
            <Bell className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>{isNotifyRequested ? 'Notified' : 'Notify Me'}</span>
          </button>
        ) : (
          <button
            onClick={() => onAddToCart(product, price)}
            className="w-full bg-gradient-to-r from-[#1a3a5c] via-[#1a3a5c] to-[#0d2240] text-white py-2 sm:py-3 rounded-full font-medium hover:from-[#0d1e33] hover:via-[#1f4268] hover:to-[#102a4a] transition-all duration-300 flex items-center justify-center space-x-1 sm:space-x-2 btn-primary shadow-md hover:shadow-lg text-xs sm:text-base mt-auto"
            data-testid={`add-to-cart-${product.id}`}
          >
            <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
            <span>Add to Cart</span>
          </button>
        )}
      </div>
    </motion.div>
  );
}